// SPDX-License-Identifier: GPL-3.0-only
// Copyright (C) 2026 Kyle Johnson
// This file is part of Hushold.
//
// Hushold is free software: you can redistribute it and/or modify
// it under the terms of the GNU General Public License as
// published by the Free Software Foundation, version 3.

import React from 'react';
import { useTranslation } from 'react-i18next';

// DECISION: The design system deliberately avoids an icon library, but a bottom
// tab bar fundamentally needs compact glyphs. These are minimal, custom-drawn,
// monochrome SVG marks in the same restrained geometric spirit as ReconcileMark:
// 1.5px strokes, no fills, sharp corners (no rounded joints), currentColor so the
// active/inactive accent coloring is driven entirely by the parent. Nothing
// decorative — each is the simplest shape that reads as its concept.

interface IconProps {
  size?: number;
  className?: string;
  strokeWidth?: number;
}

function svgProps({ size = 22, className, strokeWidth = 1.5 }: IconProps) {
  return {
    width: size,
    height: size,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth,
    strokeLinecap: 'square' as const,
    strokeLinejoin: 'miter' as const,
    className,
    'aria-hidden': true,
  };
}

// Accounts — a rotated square (ledger "diamond" mark, echoes the ◈ wordmark).
export function AccountsIcon(props: IconProps) {
  return (
    <svg {...svgProps(props)}>
      <rect x="6.5" y="6.5" width="11" height="11" transform="rotate(45 12 12)" />
      <line x1="12" y1="8.9" x2="12" y2="15.1" />
    </svg>
  );
}

// Register — stacked transaction rows (a ledger list).
export function RegisterIcon(props: IconProps) {
  return (
    <svg {...svgProps(props)}>
      <line x1="4" y1="7" x2="20" y2="7" />
      <line x1="4" y1="12" x2="20" y2="12" />
      <line x1="4" y1="17" x2="14" y2="17" />
    </svg>
  );
}

// Add — plain plus. Rendered inside the accent center button.
export function AddIcon(props: IconProps) {
  return (
    <svg {...svgProps(props)}>
      <line x1="12" y1="5" x2="12" y2="19" />
      <line x1="5" y1="12" x2="19" y2="12" />
    </svg>
  );
}

// Reports — ascending bars.
export function ReportsIcon(props: IconProps) {
  return (
    <svg {...svgProps(props)}>
      <line x1="6" y1="20" x2="6" y2="13" />
      <line x1="12" y1="20" x2="12" y2="8" />
      <line x1="18" y1="20" x2="18" y2="4" />
    </svg>
  );
}

// More — overflow (three dots), drawn as small squares to keep the sharp aesthetic.
export function MoreIcon({ size = 22, className }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden>
      <rect x="3" y="10.5" width="3" height="3" />
      <rect x="10.5" y="10.5" width="3" height="3" />
      <rect x="18" y="10.5" width="3" height="3" />
    </svg>
  );
}

// Chevron used for back navigation and expandable rows.
export function ChevronLeftIcon(props: IconProps) {
  return (
    <svg {...svgProps(props)}>
      <polyline points="15 5 8 12 15 19" />
    </svg>
  );
}

export function ChevronRightIcon(props: IconProps) {
  return (
    <svg {...svgProps(props)}>
      <polyline points="9 5 16 12 9 19" />
    </svg>
  );
}

export function ChevronDownIcon(props: IconProps) {
  return (
    <svg {...svgProps(props)}>
      <polyline points="5 9 12 16 19 9" />
    </svg>
  );
}

// Search — magnifier for the account/report search affordances.
export function SearchIcon(props: IconProps) {
  return (
    <svg {...svgProps(props)}>
      <circle cx="10.5" cy="10.5" r="6" />
      <line x1="15" y1="15" x2="20" y2="20" />
    </svg>
  );
}

// Close — X for full-screen sheets.
export function CloseIcon(props: IconProps) {
  return (
    <svg {...svgProps(props)}>
      <line x1="6" y1="6" x2="18" y2="18" />
      <line x1="18" y1="6" x2="6" y2="18" />
    </svg>
  );
}

// Menu — three stacked lines (hamburger) for the small-screen sidebar toggle.
export function MenuIcon(props: IconProps) {
  return (
    <svg {...svgProps(props)}>
      <line x1="4" y1="7" x2="20" y2="7" />
      <line x1="4" y1="12" x2="20" y2="12" />
      <line x1="4" y1="17" x2="20" y2="17" />
    </svg>
  );
}

// Hushold mark — the brand logomark (an H whose crossbar is a doubled equals
// sign). Always drawn in copper (#BE8250); it is deliberately not tied to the
// `accent` token so the register keeps green/red free for balances.
export function HusholdMark({ size = 20, className }: Pick<IconProps, 'size' | 'className'>) {
  const { t } = useTranslation(['common', 'mobile', 'errors']);
  return (
    <svg
      width={size}
      height={size}
      viewBox="15 10 70 80"
      role="img"
      aria-label={t('appName')}
      className={className}
    >
      <rect x="23" y="18" width="11" height="64" fill="#BE8250" />
      <rect x="66" y="18" width="11" height="64" fill="#BE8250" />
      <rect x="40" y="35" width="20" height="9" fill="#BE8250" />
      <rect x="40" y="56" width="20" height="9" fill="#BE8250" />
    </svg>
  );
}
